import { X } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useBrands } from '@/features/brands';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value';

import type { CouponBrand } from '../types/coupons.types';

const NO_OPTIONS: SearchableOption[] = [];

/**
 * Which brands a coupon or points tier covers: every brand, or a list. The list is a picker that
 * adds one brand at a time and chips that remove them.
 */
export function BrandScopeField({
  idPrefix,
  countryId,
  appliesToAllBrands,
  brandIds,
  knownBrands,
  onChange,
  error,
  disabled = false,
}: {
  idPrefix: string;
  countryId: number;
  appliesToAllBrands: boolean;
  brandIds: number[];
  /** Names of brands already on the coupon (shown before the picker has loaded them). */
  knownBrands: CouponBrand[];
  onChange: (value: { appliesToAllBrands: boolean; brandIds: number[] }) => void;
  error?: string;
  disabled?: boolean;
}) {
  const [search, setSearch] = useState('');
  const debounced = useDebouncedValue(search, 300);
  const [names, setNames] = useState<Record<number, string>>(() =>
    Object.fromEntries(knownBrands.map((b) => [b.brandId, b.name])),
  );
  const brands = useBrands({ page: 1, pageSize: 20, search: debounced || undefined, countryId });

  const options =
    brands.data?.items
      .filter((brand) => !brandIds.includes(brand.id))
      .map((brand) => ({ value: String(brand.id), label: brand.name })) ?? NO_OPTIONS;

  const add = (value: string) => {
    const id = Number(value);
    const brand = brands.data?.items.find((b) => b.id === id);
    if (brand) setNames((current) => ({ ...current, [id]: brand.name }));
    onChange({ appliesToAllBrands: false, brandIds: [...brandIds, id] });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Switch
          id={`${idPrefix}-all-brands`}
          checked={appliesToAllBrands}
          onCheckedChange={(checked) => onChange({ appliesToAllBrands: checked, brandIds })}
          disabled={disabled}
        />
        <Label htmlFor={`${idPrefix}-all-brands`} className="text-xs">
          Applies to every brand
        </Label>
      </div>
      {!appliesToAllBrands && (
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}-brand`} className="text-xs">
            Brands
          </Label>
          <SearchableSelect
            id={`${idPrefix}-brand`}
            label="Add a brand"
            value={null}
            options={options}
            onChange={add}
            placeholder="Add a brand"
            searchPlaceholder="Search brands…"
            emptyText="No brand matches."
            search={search}
            onSearchChange={setSearch}
            loading={brands.isFetching}
            disabled={disabled}
            triggerClassName="h-9 w-full text-sm"
          />
          {brandIds.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label="Chosen brands">
              {brandIds.map((id) => (
                <li
                  key={id}
                  className="bg-muted flex items-center gap-1 rounded-md py-0.5 ps-2 pe-0.5 text-xs"
                >
                  {names[id] ?? `Brand #${id}`}
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="size-5"
                    aria-label={`Remove ${names[id] ?? `brand #${id}`}`}
                    disabled={disabled}
                    onClick={() =>
                      onChange({
                        appliesToAllBrands: false,
                        brandIds: brandIds.filter((b) => b !== id),
                      })
                    }
                  >
                    <X className="size-3" aria-hidden />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {error && (
        <p role="alert" className="text-destructive text-xs">
          {error}
        </p>
      )}
    </div>
  );
}
